import classNames from 'classnames';
import React, {useState} from 'react';

import {FormulaAlignment} from 'notebook/tables/TableConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

export interface TextAlignmentDropdownProps {
  selected?: FormulaAlignment;
  iconFormat: FormulaAlignment;
  disabled: boolean;
  onSelect: (alignment?: FormulaAlignment) => void;
}

const getAlignmentDetails = (alignment: FormulaAlignment): {icon: BenchlingIconName; altText: string} => {
  switch (alignment) {
    case 'left':
      return {icon: 'format-left', altText: 'Align left'};
    case 'right':
      return {icon: 'format-right', altText: 'Align right'};
    case 'center':
      return {icon: 'format-center', altText: 'Align center'};
    default:
      return assertNever(alignment);
  }
};

export function TextAlignmentDropdown({
  selected,
  iconFormat,
  disabled,
  onSelect,
}: TextAlignmentDropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const alignmentDetails = getAlignmentDetails(selected ?? iconFormat);

  const handleFormattingClick = (value?: FormulaAlignment): void => {
    if (selected === value) {
      onSelect(undefined);
    } else {
      onSelect(value);
    }
    setIsOpen(false);
  };

  return (
    <AbsoluteDropdownContainer
      open={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
      }}
      menu={
        <ul className="dropdown-menu dropdown-menu--horizontal" role="menu" aria-label="Alignment menu">
          <AlignmentOption alignment="left" onClick={handleFormattingClick} />
          <AlignmentOption alignment="center" onClick={handleFormattingClick} />
          <AlignmentOption alignment="right" onClick={handleFormattingClick} />
        </ul>
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className={classNames({
          'is-active': Boolean(selected),
          'is-disabled': disabled,
        })}
        disabled={disabled}
        icon={alignmentDetails.icon}
        iconAltText={alignmentDetails.altText}
        isDropdown={true}
        size={ButtonSize.MINI}
        onClick={() => {
          setIsOpen(true);
        }}
        dataTestAttrs={{component: 'FormulaBox', element: 'formatAlignDropdown'}}
      />
    </AbsoluteDropdownContainer>
  );
}

function AlignmentOption({
  alignment,
  onClick,
}: {
  alignment: FormulaAlignment;
  onClick: (alignment: FormulaAlignment) => void;
}): JSX.Element {
  const {icon, altText} = getAlignmentDetails(alignment);
  return (
    <li>
      <a
        role="menuitem"
        tabIndex={-1}
        onClick={() => {
          onClick(alignment);
        }}
        {...getTestProps({component: 'FormulaBox', element: 'formatAlignDropdownOption', key: alignment})}
      >
        <BenchlingIcon icon={icon} altText={altText} />
      </a>
    </li>
  );
}
